import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Package, Heart, User as UserIcon } from "lucide-react";
import { api, cad } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (user === false) navigate("/login");
    if (user) api.get("/orders").then(({ data }) => setOrders(data));
  }, [user, navigate]);

  if (!user) return <Layout><div className="py-40 text-center text-charcoal/50">Loading…</div></Layout>;

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/");
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-16 py-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="eyebrow">My Account</p>
            <h1 className="mt-2 font-serif text-4xl text-charcoal">Hello, {user.name}</h1>
          </div>
          <Button data-testid="logout-btn" onClick={doLogout} variant="outline" className="rounded-none border-charcoal font-display tracking-wide">
            <LogOut className="h-4 w-4 mr-2" /> Sign Out
          </Button>
        </div>

        {user.role === "admin" && (
          <div className="mb-8 bg-gold-light border border-gold/30 rounded-sm p-5 flex items-center justify-between">
            <p className="text-sm text-charcoal">You're signed in as an admin.</p>
            <Button asChild className="bg-charcoal text-white rounded-none hover:bg-gold font-display">
              <Link to="/admin" data-testid="go-admin-btn">Open Admin</Link>
            </Button>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4 mb-12">
          <InfoCard icon={UserIcon} label="Email" value={user.email} />
          <InfoCard icon={Package} label="Orders" value={orders.length} />
          <Link to="/wishlist"><InfoCard icon={Heart} label="Wishlist" value={`${user.wishlist?.length || 0} saved`} clickable /></Link>
        </div>

        <h2 className="font-serif text-2xl text-charcoal mb-6">Order History</h2>
        {orders.length === 0 ? (
          <p className="text-charcoal/50 text-sm" data-testid="no-orders">No orders yet — your future favourites are waiting.</p>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.order_id} className="bg-white border border-soft-border rounded-sm p-5" data-testid={`order-${o.order_id}`}>
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <div>
                    <p className="font-serif text-lg text-charcoal">Order #{o.order_id}</p>
                    <p className="text-xs text-charcoal/50">{new Date(o.created_at).toLocaleDateString()} · {o.items.length} item(s)</p>
                  </div>
                  <div className="text-right">
                    <span className="eyebrow bg-blush px-3 py-1 rounded-full capitalize">{o.status}</span>
                    <p className="font-serif text-lg text-charcoal mt-1">{cad(o.total)}</p>
                  </div>
                </div>
                <div className="mt-3 text-sm text-charcoal/60">
                  {o.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

const InfoCard = ({ icon: Icon, label, value, clickable }) => (
  <div className={`bg-white border border-soft-border rounded-sm p-5 ${clickable ? "hover-lift cursor-pointer" : ""}`}>
    <Icon className="h-5 w-5 text-gold" />
    <p className="eyebrow text-[0.6rem] mt-3">{label}</p>
    <p className="text-charcoal mt-1 truncate">{value}</p>
  </div>
);
